import { useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';

import classNames from '@utils/classnames';
import { useTranslation } from 'contexts/translation-context';

/**
 * The width the panel keeps until the reader drags the divider: a share of the row, so a wider window gets
 * a wider map, never narrower than the width the list beside it was designed against, and capped so a very
 * wide window does not leave the list stranded.
 */
const DEFAULT_PANEL_WIDTH = 'clamp(440px, 40%, 560px)';

/** Where the drag stops: below this the map and the fields stop fitting, above it the list is crushed. */
const MIN_PANEL_WIDTH = 320;
const MIN_MAIN_WIDTH = 320;

/**
 * The most of the row the panel may take, so a width set on a wide window cannot squeeze the list off the
 * row once the window narrows again. The fluid default stays under this share at every width the two
 * columns sit side by side, so it only ever limits a width the reader set themselves.
 */
const MAX_PANEL_SHARE = 0.6;

/** How far one arrow key moves the divider. */
const KEYBOARD_STEP = 32;

const readStoredWidth = (storageKey: string): number | null => {
  try {
    const stored = Number(globalThis.localStorage.getItem(storageKey));
    return Number.isFinite(stored) && stored > 0 ? stored : null;
  } catch {
    return null;
  }
};

const writeStoredWidth = (storageKey: string, width: number | null) => {
  try {
    if (width === null) {
      globalThis.localStorage.removeItem(storageKey);
    } else {
      globalThis.localStorage.setItem(storageKey, String(Math.round(width)));
    }
  } catch {
    // Storage is a convenience only.
  }
};

interface IResizableSidePanelProps {
  /** The panel that sits beside the main column. Left out, the main column takes the whole row. */
  side?: ReactNode;
  /** Where the reader's chosen width is remembered, so it survives a reload. */
  storageKey: string;
  children: ReactNode;
}

/**
 * A main column with a panel beside it, and a divider between them the reader can drag.
 *
 * The panel takes a share of the row by default — it is as wide as the window allows rather than pinned
 * to one number — and the divider overrides that with an explicit width, which is remembered per
 * `storageKey`. Dragging is offered from `xl` up only, because below that the two columns stack and there
 * is nothing to divide.
 */
export const ResizableSidePanel = ({ side, storageKey, children }: IResizableSidePanelProps) => {
  const { i18n } = useTranslation();
  const rowRef = useRef<HTMLDivElement>(null);
  const mainRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  /**
   * The width the current drag has moved to, or `null` while it has not moved at all — which is how a plain
   * click on the divider is told from a drag, and what a cancelled drag falls back on.
   */
  const draggedWidthRef = useRef<number | null>(null);
  const [width, setWidth] = useState<number | null>(() => readStoredWidth(storageKey));
  const [isDragging, setIsDragging] = useState(false);

  /** The width a drag at this pointer position asks for, kept inside what the row can spare. */
  const widthFromPointer = (clientX: number): number => {
    const row = rowRef.current?.getBoundingClientRect();
    if (!row) {
      return MIN_PANEL_WIDTH;
    }
    return clampWidth(row.right - clientX);
  };

  const clampWidth = (next: number): number => {
    const row = rowRef.current?.getBoundingClientRect();
    const mainColumn = mainRef.current?.getBoundingClientRect();
    const panelColumn = panelRef.current?.getBoundingClientRect();
    if (!row || !mainColumn || !panelColumn) {
      return Math.max(next, MIN_PANEL_WIDTH);
    }

    // What the divider itself occupies — the gap either side of it and its own width — measured rather
    // than repeated here, so the spacing tokens stay the only place those numbers are written down.
    const dividerSpace = panelColumn.left - mainColumn.right;
    // Whichever limit bites first: the list keeps a width it can be read at, and on a wide row it keeps a
    // share of that row, so the panel cannot grow with the window until the list is a sliver of it.
    const widest = Math.max(
      MIN_PANEL_WIDTH,
      Math.min(row.width - dividerSpace - MIN_MAIN_WIDTH, row.width * MAX_PANEL_SHARE),
    );

    return Math.min(Math.max(next, MIN_PANEL_WIDTH), widest);
  };

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    // Keeps the drag from selecting the page instead of the divider, which would otherwise also cost the
    // divider the focus a click on it should give a keyboard reader.
    event.preventDefault();
    event.currentTarget.focus();
    event.currentTarget.setPointerCapture(event.pointerId);
    draggedWidthRef.current = null;
    setIsDragging(true);
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }
    const next = widthFromPointer(event.clientX);
    draggedWidthRef.current = next;
    setWidth(next);
  };

  /** Ends the drag on the width the pointer left it at, which is what gets remembered. */
  const handlePointerUp = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }
    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsDragging(false);

    if (draggedWidthRef.current === null) {
      return;
    }

    const finalWidth = widthFromPointer(event.clientX);
    setWidth(finalWidth);
    writeStoredWidth(storageKey, finalWidth);
  };

  /**
   * A gesture the browser took away ends the drag where it last was. Its coordinates are not a position to
   * measure from — engines report a cancelled pointer at zero, which would read as the widest the row allows.
   */
  const handlePointerCancel = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) {
      return;
    }
    event.currentTarget.releasePointerCapture(event.pointerId);
    setIsDragging(false);

    if (draggedWidthRef.current !== null) {
      writeStoredWidth(storageKey, draggedWidthRef.current);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    // The panel is to the right of the divider, so the left arrow widens it.
    const direction = event.key === 'ArrowLeft' ? 1 : event.key === 'ArrowRight' ? -1 : 0;
    if (direction === 0) {
      return;
    }
    event.preventDefault();

    // A reader who has never dragged starts from the width the clamp gave the panel, and one who has may be
    // looking at a width the row has since capped, so either way the press moves what they can see.
    const from = panelRef.current?.getBoundingClientRect().width ?? width ?? MIN_PANEL_WIDTH;
    const nextWidth = clampWidth(from + direction * KEYBOARD_STEP);
    setWidth(nextWidth);
    writeStoredWidth(storageKey, nextWidth);
  };

  /** Back to the share of the row: the way out of a width that turned out to be wrong. */
  const handleDoubleClick = () => {
    setWidth(null);
    writeStoredWidth(storageKey, null);
  };

  // A drag re-renders this component on every pointer move, and the two columns are the elements the parent
  // handed over: React sees the same objects and leaves both subtrees — the map included — alone.
  return (
    <div
      ref={rowRef}
      className={classNames('grid items-start gap-4', side && 'xl:grid-cols-[minmax(0,1fr)_auto_var(--panel-width)]')}
      // The panel's width, as a length the column above reads. The `clamp()` is the default the reader has
      // not overridden; a drag replaces it with the pixels they chose.
      style={
        side ? ({ '--panel-width': width === null ? DEFAULT_PANEL_WIDTH : `${width}px` } as CSSProperties) : undefined
      }
    >
      <div ref={mainRef} className="flex flex-col gap-4">
        {children}
      </div>

      {side && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label={i18n('liveTracking.resizePanel')}
          // Only a width the reader set is a number worth announcing; until then the clamp decides it,
          // and how wide it may be depends on the window the drag is measured against.
          aria-valuenow={width ?? undefined}
          aria-valuemin={MIN_PANEL_WIDTH}
          tabIndex={0}
          title={i18n('liveTracking.resizePanel')}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerCancel}
          onDoubleClick={handleDoubleClick}
          onKeyDown={handleKeyDown}
          className={classNames(
            'group hidden w-3 cursor-col-resize touch-none items-center justify-center self-stretch rounded-full xl:flex',
            'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
          )}
        >
          <span
            className={classNames(
              'h-full w-0.5 rounded-full transition-colors',
              isDragging ? 'bg-brand' : 'bg-border group-hover:bg-brand',
            )}
          />
        </div>
      )}

      {side && <div ref={panelRef}>{side}</div>}
    </div>
  );
};
